/**
 * usePreservingRevalidator Hook
 *
 * Wrapper around React Router's useRevalidator that preserves scroll position.
 * Use this instead of useRevalidator when you need scroll preservation during
 * data revalidation (e.g., after dismiss/resolve actions).
 *
 * Problem: When revalidator.revalidate() is called, React Router re-runs the
 * loader which causes a full page re-render, resetting scroll to top.
 *
 * Solution: Capture scroll position before revalidation, then restore it
 * after the revalidation completes using double requestAnimationFrame to
 * ensure the DOM has fully updated.
 */

import { useRevalidator } from 'react-router';
import { useCallback, useRef, useEffect } from 'react';

interface ScrollPosition {
  x: number;
  y: number;
}

/**
 * Get current scroll position with cross-browser and iframe compatibility
 */
function getScrollPosition(): ScrollPosition {
  return {
    x: window.scrollX || window.pageXOffset || document.documentElement.scrollLeft || 0,
    y: window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0,
  };
}

/**
 * Restore scroll position with clamping to valid range
 * Handles cases where content height has changed (e.g., after dismissing an alert)
 */
function setScrollPosition(position: ScrollPosition): void {
  const maxScrollY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
  const maxScrollX = Math.max(0, document.documentElement.scrollWidth - window.innerWidth);

  window.scrollTo({
    top: Math.min(position.y, maxScrollY),
    left: Math.min(position.x, maxScrollX),
    behavior: 'auto', // Instant - no animation
  });
}

/**
 * Return type for usePreservingRevalidator hook
 */
export interface UsePreservingRevalidatorReturn {
  /** Current revalidation state: 'idle' or 'loading' */
  state: 'idle' | 'loading';
  /** Trigger revalidation with scroll preservation */
  revalidate: () => void;
  /** Trigger revalidation without scroll preservation (for intentional scroll resets) */
  revalidateWithoutScrollPreservation: () => void;
}

/**
 * Hook that wraps React Router's useRevalidator with scroll position preservation
 *
 * @returns Revalidator with scroll-preserving revalidate function
 *
 * @example
 * ```tsx
 * const revalidator = usePreservingRevalidator();
 *
 * async function handleDismiss(alertId: number) {
 *   await dismissAlert(alertId);
 *   revalidator.revalidate(); // Scroll position is preserved
 * }
 * ```
 */
export function usePreservingRevalidator(): UsePreservingRevalidatorReturn {
  const revalidator = useRevalidator();
  const pendingScrollPosition = useRef<ScrollPosition | null>(null);
  const wasRevalidating = useRef(false);

  // Watch for revalidation completion and restore scroll
  useEffect(() => {
    const isRevalidating = revalidator.state === 'loading';

    // When revalidation completes (was loading, now idle), restore scroll
    if (wasRevalidating.current && !isRevalidating && pendingScrollPosition.current) {
      // Use double requestAnimationFrame to ensure DOM has fully updated
      // First rAF waits for React to commit changes
      // Second rAF waits for browser to paint
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (pendingScrollPosition.current) {
            setScrollPosition(pendingScrollPosition.current);
            pendingScrollPosition.current = null;
          }
        });
      });
    }

    wasRevalidating.current = isRevalidating;
  }, [revalidator.state]);

  // Wrapped revalidate that captures scroll first
  const revalidate = useCallback(() => {
    pendingScrollPosition.current = getScrollPosition();
    revalidator.revalidate();
  }, [revalidator]);

  return {
    state: revalidator.state,
    revalidate,
    revalidateWithoutScrollPreservation: revalidator.revalidate,
  };
}
